import { Loader2 } from "lucide-react";
import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { LOGO_URL } from "@/lib/branding";

/** Pide el correo y dispara el envío del link de recuperación. Siempre se responde
 *  200 aunque el correo no exista (para no filtrar usuarios). */
function ForgotPasswordView() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="space-y-4 text-center" data-testid="forgot-sent-view">
        <p className="text-sm">
          Si el correo <span className="font-semibold">{email}</span> está registrado, te enviamos un
          link para restablecer tu contraseña. Revisa tu bandeja de entrada (y la carpeta de spam).
        </p>
        <Link to="/auth" className="text-primary text-sm font-semibold underline" data-testid="forgot-back-to-login">
          Volver al inicio de sesión
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
      <p className="text-sm text-muted-foreground">
        Ingresa el correo de tu cuenta. Te enviaremos un link para crear una nueva contraseña.
      </p>
      <div className="space-y-1.5">
        <Label>Correo electrónico</Label>
        <Input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="forgot-email-input"
        />
      </div>
      <Button type="submit" className="w-full rounded-full" disabled={loading} data-testid="forgot-submit-btn">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enviar link de recuperación"}
      </Button>
      <div className="text-center">
        <Link to="/auth" className="text-sm text-muted-foreground hover:text-primary" data-testid="forgot-cancel-link">
          Cancelar
        </Link>
      </div>
    </form>
  );
}

/** Recibe ?token= y permite al usuario crear una nueva contraseña. Tras éxito el
 *  backend ya setea las cookies, así que entramos directo al dashboard. */
function ResetPasswordView({ token }) {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres.");
      return;
    }
    if (password !== confirm) {
      toast.error("Las contraseñas no coinciden.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      toast.success("Contraseña actualizada");
      if (data?.user_id) {
        setUser(data);
        navigate(data.role ? "/dashboard" : "/completar-perfil", { replace: true });
      } else {
        navigate("/auth", { replace: true });
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
      <p className="text-sm text-muted-foreground">
        Crea una nueva contraseña para tu cuenta.
      </p>
      <div className="space-y-1.5">
        <Label>Nueva contraseña</Label>
        <Input
          type="password"
          required
          minLength={6}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="reset-password-input"
        />
      </div>
      <div className="space-y-1.5">
        <Label>Confirmar contraseña</Label>
        <Input
          type="password"
          required
          minLength={6}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          data-testid="reset-confirm-input"
        />
      </div>
      <Button type="submit" className="w-full rounded-full" disabled={loading} data-testid="reset-submit-btn">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar y entrar"}
      </Button>
    </form>
  );
}

export default function PasswordResetPage({ mode }) {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const token = params.get("token");

  if (user) return <Navigate to={user.role ? "/dashboard" : "/completar-perfil"} replace />;

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4 py-12">
      <Link to="/" className="flex items-center gap-2 mb-8" data-testid="reset-logo-link">
        <img src={LOGO_URL} alt="TransSalud" className="h-10 w-10 object-contain" />
        <span className="font-heading font-semibold text-xl">
          TransSalud <span className="text-primary">· THAG</span>
        </span>
      </Link>

      <div className="w-full max-w-md bg-white border rounded-2xl p-6 sm:p-8">
        <h1 className="font-heading text-xl font-bold mb-4" data-testid="reset-page-title">
          {mode === "reset" ? "Restablecer contraseña" : "¿Olvidaste tu contraseña?"}
        </h1>
        {mode === "reset" ? (
          token ? (
            <ResetPasswordView token={token} />
          ) : (
            <p className="text-sm text-muted-foreground">
              El link no es válido. <Link to="/recuperar-contrasena" className="text-primary underline">Solicita uno nuevo</Link>.
            </p>
          )
        ) : (
          <ForgotPasswordView />
        )}
      </div>
    </div>
  );
}
